import { useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Eye, Download, Calendar, Loader2 } from "lucide-react"
import { useAuth } from "@/hooks/useAuth"
import { useEmployeeTimesheetReport } from "@/hooks/useTimesheetRecords"
import { useCondominium } from "@/hooks/useCondominium"
import { generateTimesheetPDF } from "@/utils/pdfGenerator"
import { toast } from "sonner"

export const OperadorTimesheetView = () => {
  const { profile } = useAuth()
  const { data: condominium } = useCondominium()
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState<{ start: string; end: string } | null>(null)

  // Generate last 12 months options
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date()
    date.setMonth(date.getMonth() - i)
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const monthName = date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
    return {
      value: `${year}-${month}`,
      label: monthName.charAt(0).toUpperCase() + monthName.slice(1),
      year,
      month: parseInt(month)
    }
  })

  // Calculate start and end dates for selected month
  const getMonthDates = (yearMonth: string) => {
    const [year, month] = yearMonth.split('-').map(Number)
    const startDate = new Date(year, month - 1, 1)
    const endDate = new Date(year, month, 0)
    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    }
  }

  const currentDates = getMonthDates(selectedMonth)
  const { data: timesheetData, isLoading } = useEmployeeTimesheetReport(
    profile?.id || '',
    currentDates.start,
    currentDates.end
  )

  const handleViewDetails = () => {
    setSelectedPeriod(currentDates)
    setViewModalOpen(true)
  }

  const handleDownloadPDF = async () => {
    if (!timesheetData || !profile || !condominium) {
      toast.error('Dados incompletos para gerar PDF')
      return
    }

    try {
      await generateTimesheetPDF(
        timesheetData,
        {
          full_name: profile.full_name || 'Funcionário',
          cpf: profile.cpf || null,
          phone: profile.phone || null,
          email: profile.email,
          role: profile.role
        },
        {
          name: condominium.name,
          address: condominium.address || undefined
        },
        currentDates.start,
        currentDates.end
      )
      toast.success('PDF gerado com sucesso!')
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      toast.error('Erro ao gerar PDF')
    }
  }

  const formatTime = (timestamp: string | null) => {
    if (!timestamp) return '-'
    const date = new Date(timestamp)
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  const parseInterval = (interval: string | null): number => {
    if (!interval) return 0
    
    if (interval.includes('seconds')) {
      const seconds = parseFloat(interval.split(' ')[0])
      return seconds / 3600
    }
    
    const parts = interval.split(':')
    if (parts.length === 3) {
      const hours = parseInt(parts[0])
      const minutes = parseInt(parts[1])
      return hours + (minutes / 60)
    }
    
    return 0
  }

  const formatHoursMinutes = (hours: number) => {
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    
    if (minutes === 0) {
      return `${wholeHours}h`
    }
    return `${wholeHours}h ${minutes}m`
  }

  const totalHours = timesheetData?.reduce((sum, record) => {
    return sum + parseInterval(record.total_horas)
  }, 0) || 0

  const selectedMonthName = monthOptions.find(m => m.value === selectedMonth)?.label || ''

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Folhas de Ponto</h1>
        <p className="text-muted-foreground">Histórico e downloads</p>
      </div>

      <div className="flex items-center gap-4">
        <Calendar className="h-5 w-5 text-muted-foreground" />
        <Select value={selectedMonth} onValueChange={setSelectedMonth}>
          <SelectTrigger className="w-[280px]">
            <SelectValue placeholder="Selecione o mês" />
          </SelectTrigger>
          <SelectContent>
            {monthOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="p-12 flex items-center justify-center">
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Carregando dados...</p>
            </div>
          </CardContent>
        </Card>
      ) : timesheetData && timesheetData.length > 0 ? (
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle>{profile?.full_name || 'Funcionário'}</CardTitle>
                <CardDescription className="mt-1">
                  {selectedMonthName} • Total: {formatHoursMinutes(totalHours)}
                </CardDescription>
              </div>
              <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
                {timesheetData.length} {timesheetData.length === 1 ? 'registro' : 'registros'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex gap-2">
              <Button onClick={handleViewDetails} variant="outline" className="flex items-center gap-2">
                <Eye className="h-4 w-4" />
                Visualizar
              </Button>
              <Button onClick={handleDownloadPDF} className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Download PDF
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">
              Nenhum registro de ponto encontrado para {selectedMonthName}
            </p>
          </CardContent>
        </Card>
      )}

      {/* View Details Modal */}
      <Dialog open={viewModalOpen} onOpenChange={setViewModalOpen}>
        <DialogContent className="max-w-6xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Folha de Ponto - {selectedMonthName}</DialogTitle>
          </DialogHeader>
          
          {timesheetData && timesheetData.length > 0 && (
            <div className="space-y-4">
              <div className="rounded-lg border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">Data</th>
                        <th className="px-4 py-3 text-left font-medium">Entrada</th>
                        <th className="px-4 py-3 text-left font-medium">Saída Almoço</th>
                        <th className="px-4 py-3 text-left font-medium">Volta Almoço</th>
                        <th className="px-4 py-3 text-left font-medium">Saída</th>
                        <th className="px-4 py-3 text-left font-medium">Total Horas</th>
                        <th className="px-4 py-3 text-left font-medium">Observações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {timesheetData.map((record, index) => (
                        <tr key={index} className="border-t hover:bg-muted/50">
                          <td className="px-4 py-3">
                            {new Date(record.date).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="px-4 py-3">{formatTime(record.entrada)}</td>
                          <td className="px-4 py-3">{formatTime(record.saida_almoco)}</td>
                          <td className="px-4 py-3">{formatTime(record.volta_almoco)}</td>
                          <td className="px-4 py-3">{formatTime(record.saida)}</td>
                          <td className="px-4 py-3 font-medium">
                            {record.total_horas ? formatHoursMinutes(parseInterval(record.total_horas)) : '-'}
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">
                            {record.observacoes || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-muted font-medium">
                      <tr>
                        <td colSpan={5} className="px-4 py-3 text-right">TOTAL:</td>
                        <td className="px-4 py-3">{formatHoursMinutes(totalHours)}</td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button onClick={() => setViewModalOpen(false)} variant="outline">
                  Fechar
                </Button>
                <Button onClick={handleDownloadPDF} className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
